import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TEACHING_LANGUAGES } from "@/lib/useCoachLanguages";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function LanguageChecks({ coachId, value, onChange }) {
  const toggle = (l) => onChange(value.includes(l) ? value.filter((x) => x !== l) : [...value, l]);
  return (
    <div className="flex flex-wrap gap-2">
      {TEACHING_LANGUAGES.map((l) => {
        const on = value.includes(l);
        return (
          <button
            key={l}
            type="button"
            onClick={() => toggle(l)}
            aria-pressed={on}
            data-testid={`admin-lang-${coachId}-${slug(l)}`}
            className={`border px-3 py-1.5 font-sans text-xs transition-colors duration-200 ${
              on ? "border-forest bg-forest text-cream-light" : "border-cream-border bg-cream text-forest hover:border-gold"
            }`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

export const AdminCoachRow = ({ coach, record, onSaved, onSessionExpired }) => {
  const [langs, setLangs] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const videoId = record?.intro_video_id;

  useEffect(() => {
    setLangs(record?.teaching_languages || []);
    setUrl(record?.intro_video_id ? `https://youtu.be/${record.intro_video_id}` : "");
  }, [record]);

  const handleError = (err) => {
    if (err?.response?.status === 401) onSessionExpired();
    toast.error(formatApiError(err));
  };

  const save = async () => {
    setBusy(true);
    try {
      const ordered = TEACHING_LANGUAGES.filter((l) => langs.includes(l));
      const v = await api.put(`/admin/coach-videos/${coach.id}`, { youtube_url: url.trim() || null });
      const l = await api.put(`/admin/coach-languages/${coach.id}`, { teaching_languages: ordered });
      onSaved({ teaching_languages: l.data.teaching_languages, intro_video_id: v.data.intro_video_id });
      toast.success(`${coach.name} saved`);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const removeVideo = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/coach-videos/${coach.id}`, { youtube_url: null });
      onSaved({ intro_video_id: null });
      toast.success(`Video removed for ${coach.name}`);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid={`admin-coach-row-${coach.id}`} className="grid gap-6 border border-cream-border bg-cream-light p-5 md:grid-cols-[160px_1fr] md:p-7">
      <img src={coach.image} alt={coach.name} className="aspect-[4/3] w-full object-cover md:w-40" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-serif text-2xl text-forest">{coach.name}</h2>
          <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-forest-muted">{coach.id}</span>
        </div>

        <div className="mt-5 eyebrow">Teaching languages</div>
        <div className="mt-3"><LanguageChecks coachId={coach.id} value={langs} onChange={setLangs} /></div>

        <div className="mt-6 eyebrow">YouTube intro video</div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid={`admin-video-input-${coach.id}`}
            className="min-w-0 flex-1 border border-cream-border bg-cream px-4 py-2.5 font-sans text-sm text-forest outline-none focus:border-gold"
          />
          {videoId && (
            <a href={`https://youtu.be/${videoId}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-video-preview-${coach.id}`} className="shrink-0">
              <img src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`} alt="Current intro video" className="h-14 w-24 object-cover" />
            </a>
          )}
        </div>
        <p className="mt-2 font-sans text-xs text-forest-muted">
          {videoId ? "Video is live on the coach card." : "No video — nothing is shown on the coach card."}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={save} disabled={busy} data-testid={`admin-save-${coach.id}`} className="btn-primary !px-6 !py-3 text-[10px] disabled:opacity-60">
            {busy ? "Saving…" : "Save changes"}
          </button>
          {videoId && (
            <button type="button" onClick={removeVideo} disabled={busy} data-testid={`admin-remove-video-${coach.id}`} className="border border-cream-border px-5 py-3 font-sans text-[10px] tracking-[0.2em] uppercase text-forest hover:border-gold disabled:opacity-60">
              Remove video
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
